import {
  PAINT_MEASURABLE_PANELS,
  PAINT_READINGS_MAX_PER_PANEL,
  PAINT_READING_MAX_UM,
  PAINT_READING_MIN_UM,
  PAINT_THIN_THRESHOLD_UM,
  isPaintMeasurablePanel,
  type PaintMeasurablePanel,
  type PaintReadings,
  type PanelReadings,
} from '@mustauto/types/paint-readings';
import { PACKAGE_MODULE } from '@mustauto/types/esthetic-packages';
import { OS_NO_PENDING_DELIVERY_STATUSES } from './os-status';

/**
 * Regras puras do laudo de espessura (F2.3). App (offline), API e telas leem
 * daqui: a peça que o app marca como fina é a mesma que o painel, o cliente e
 * a página compartilhada marcam.
 */

/** O módulo da OS tem laudo? Hoje, só a estética. */
export function moduleHasPaintReadings(module: string | null | undefined): boolean {
  return module === PACKAGE_MODULE;
}

/**
 * Ainda dá para medir? Até o carro ir embora: a saída se mede antes da
 * entrega, e depois dela (ou da OS anulada) um laudo novo seria escrito no dia
 * da reclamação — justamente o que ele não pode aceitar. `completed` e
 * `waiting_payment` ainda aceitam: o carro está no pátio.
 */
export function canEditPaintReadings(status: string | null | undefined): boolean {
  return status == null || !(OS_NO_PENDING_DELIVERY_STATUSES as readonly string[]).includes(status);
}

/** Uma leitura válida (inteiro de 1 a 2.000 µm), ou `null`. */
export function normalizeReading(raw: unknown): number | null {
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw.replace(',', '.')) : NaN;
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n);
  if (rounded < PAINT_READING_MIN_UM || rounded > PAINT_READING_MAX_UM) return null;
  return rounded;
}

/**
 * As leituras de um momento, sem confiar na origem: só peça medível, só
 * leitura válida, no máximo 5 por peça, peça vazia fora. Lixo vira `{}`.
 */
export function normalizePanelReadings(raw: unknown): PanelReadings {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: PanelReadings = {};
  for (const panel of PAINT_MEASURABLE_PANELS) {
    const values = (raw as Record<string, unknown>)[panel];
    if (!Array.isArray(values)) continue;
    const clean = values
      .map(normalizeReading)
      .filter((v): v is number => v !== null)
      .slice(0, PAINT_READINGS_MAX_PER_PANEL);
    if (clean.length > 0) out[panel] = clean;
  }
  return out;
}

/**
 * Um instante válido, em ISO, ou `null`. O horário do laudo vai para
 * `formatInstantDateBR(new Date(...))` na página compartilhada e no PDF, e
 * `Intl` LANÇA com data inválida — um `"abc"` gravado derrubaria as duas.
 */
export function normalizeReadingInstant(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

/** Chaves desconhecidas somem; o resto é normalizado. Horário inválido some. */
export function normalizePaintReadings(raw: unknown): PaintReadings {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const r = raw as Record<string, unknown>;
  const out: PaintReadings = {};
  const entry = normalizePanelReadings(r['entry']);
  const exit = normalizePanelReadings(r['exit']);
  if (Object.keys(entry).length > 0) {
    out.entry = entry;
    const at = normalizeReadingInstant(r['entryAt']);
    if (at) out.entryAt = at;
  }
  if (Object.keys(exit).length > 0) {
    out.exit = exit;
    const at = normalizeReadingInstant(r['exitAt']);
    if (at) out.exitAt = at;
  }
  return out;
}

export function hasPanelReadings(readings: PanelReadings | null | undefined): boolean {
  return !!readings && Object.values(readings).some((v) => Array.isArray(v) && v.length > 0);
}

/**
 * A menor leitura da peça — é a que decide o risco: o ponto fino é onde o
 * polimento atravessa o verniz, e uma média o esconde.
 */
export function panelMinReading(values: readonly number[] | null | undefined): number | null {
  if (!values || values.length === 0) return null;
  return Math.min(...values);
}

/** A leitura está abaixo do limite de pintura fina (90 µm)? */
export function isThinReading(um: number | null | undefined): boolean {
  return typeof um === 'number' && um < PAINT_THIN_THRESHOLD_UM;
}

export interface PanelReadingSummary {
  panel: PaintMeasurablePanel;
  values: number[];
  min: number;
  thin: boolean;
  /** Menor da saída − menor da entrada (negativo = o serviço tirou). Só com as duas. */
  deltaMin: number | null;
}

/**
 * Uma linha por peça medida, na ordem de volta do carro. Com entrada e saída,
 * a linha é da peça de UMA fase (a pedida) e traz a diferença de menores.
 */
export function summarizePanelReadings(
  readings: PaintReadings | null | undefined,
  phase: 'entry' | 'exit',
): PanelReadingSummary[] {
  const current = readings?.[phase] ?? {};
  const other = phase === 'exit' ? readings?.entry ?? {} : null;
  const rows: PanelReadingSummary[] = [];
  for (const panel of PAINT_MEASURABLE_PANELS) {
    const values = current[panel];
    const min = panelMinReading(values);
    if (!values || min === null) continue;
    const otherMin = other ? panelMinReading(other[panel]) : null;
    rows.push({
      panel,
      values: [...values],
      min,
      thin: isThinReading(min),
      deltaMin: otherMin !== null ? min - otherMin : null,
    });
  }
  return rows;
}

/** Uma linha da tabela Entrada × Saída: a peça, com o que cada fase mediu. */
export interface PanelReadingPair {
  panel: PaintMeasurablePanel;
  entry: PanelReadingSummary | null;
  /** Traz `deltaMin` quando a entrada também foi medida. */
  exit: PanelReadingSummary | null;
}

/**
 * A tabela do laudo — toda peça medida em alguma fase, na ordem de volta do
 * carro. Fonte única das cinco telas que mostram o laudo (app do mecânico,
 * app do cliente, painel, página compartilhada, PDF).
 */
export function pairPanelReadings(readings: PaintReadings | null | undefined): PanelReadingPair[] {
  const entry = new Map(summarizePanelReadings(readings, 'entry').map((r) => [r.panel, r]));
  const exit = new Map(summarizePanelReadings(readings, 'exit').map((r) => [r.panel, r]));
  return PAINT_MEASURABLE_PANELS.filter((p) => entry.has(p) || exit.has(p)).map((panel) => ({
    panel,
    entry: entry.get(panel) ?? null,
    exit: exit.get(panel) ?? null,
  }));
}

/**
 * "Entrada medida em 12/09/2026, saída em 14/09/2026" — ou `''`. O formatador
 * vem de quem escreve: a API usa o fuso da oficina (Regra 13); o painel, o do
 * navegador.
 */
export function describeReadingDates(
  readings: PaintReadings | null | undefined,
  formatDate: (d: Date) => string,
): string {
  const at = (iso: string | null | undefined) => {
    const norm = normalizeReadingInstant(iso);
    return norm ? formatDate(new Date(norm)) : null;
  };
  const entry = at(readings?.entryAt);
  const exit = at(readings?.exitAt);
  return [entry ? `Entrada medida em ${entry}` : null, exit ? `${entry ? 'saída' : 'Saída'} em ${exit}` : null]
    .filter((t): t is string => t !== null)
    .join(', ');
}

/** "4 peças medidas · 1 fina" — ou "Nenhuma peça medida". */
export function describePanelReadings(readings: PanelReadings | null | undefined): string {
  const measured = readings
    ? PAINT_MEASURABLE_PANELS.filter((p) => (readings[p]?.length ?? 0) > 0).length
    : 0;
  if (measured === 0) return 'Nenhuma peça medida';
  const thin = thinPanelCount(readings);
  const base = `${measured} ${measured === 1 ? 'peça medida' : 'peças medidas'}`;
  return thin > 0 ? `${base} · ${thin} ${thin === 1 ? 'fina' : 'finas'}` : base;
}

/**
 * As leituras com o que ficou DIGITADO e não confirmado em cada peça. Quem
 * digita o último ponto e toca em "Salvar" sem tocar no "+" espera que ele
 * conte — e o botão de salvar não tira o foco do campo. Valor inválido ou
 * peça cheia (5 pontos) fica de fora, como no "+".
 */
export function withPendingReadings(
  readings: PanelReadings,
  pending: Partial<Record<PaintMeasurablePanel, string>>,
): PanelReadings {
  const out: PanelReadings = { ...readings };
  for (const panel of PAINT_MEASURABLE_PANELS) {
    const value = normalizeReading(pending[panel]);
    const current = out[panel] ?? [];
    if (value === null || current.length >= PAINT_READINGS_MAX_PER_PANEL) continue;
    out[panel] = [...current, value];
  }
  return out;
}

/** Quantas peças da fase estão finas. */
export function thinPanelCount(readings: PanelReadings | null | undefined): number {
  if (!readings) return 0;
  let n = 0;
  for (const panel of Object.keys(readings)) {
    if (!isPaintMeasurablePanel(panel)) continue;
    if (isThinReading(panelMinReading(readings[panel]))) n += 1;
  }
  return n;
}

/** "1.240 µm" — mícron com separador de milhar. */
export function formatMicrons(um: number): string {
  return `${um.toLocaleString('pt-BR')} µm`;
}
